import { format, lastDayOfMonth, subDays, subMonths } from 'date-fns'

export const DATE_FORMAT = 'yyyy-MM-dd'

export type PeriodKey = 'last30' | 'last12m' | 'lastTaxYear' | 'custom'

export interface DateRange {
  startDate: string
  endDate: string
}

export interface StatementPeriod {
  key: Exclude<PeriodKey, 'custom'>
  label: string
  range: (today: Date) => DateRange
}

const MARCH = 2
const FEBRUARY = 1

/**
 * The South African tax year runs 1 March – end of February. This resolves to the most recently
 * *completed* one, so on 2026-08-18 it gives 2025-03-01 .. 2026-02-28.
 */
const lastTaxYearRange = (today: Date): DateRange => {
  const currentTaxYearStartsIn = today.getMonth() >= MARCH ? today.getFullYear() : today.getFullYear() - 1

  return {
    startDate: format(new Date(currentTaxYearStartsIn - 1, MARCH, 1), DATE_FORMAT),
    endDate: format(lastDayOfMonth(new Date(currentTaxYearStartsIn, FEBRUARY, 1)), DATE_FORMAT),
  }
}

export const STATEMENT_PERIODS: StatementPeriod[] = [
  {
    key: 'last30',
    label: 'Last 30 days',
    range: today => ({
      startDate: format(subDays(today, 30), DATE_FORMAT),
      endDate: format(today, DATE_FORMAT),
    }),
  },
  {
    key: 'last12m',
    label: 'Last 12 months',
    range: today => ({
      startDate: format(subMonths(today, 12), DATE_FORMAT),
      endDate: format(today, DATE_FORMAT),
    }),
  },
  {
    key: 'lastTaxYear',
    label: 'Last tax year',
    range: lastTaxYearRange,
  },
]

export const DEFAULT_PERIOD = STATEMENT_PERIODS[0]

/**
 * The selected preset is derived from the range rather than stored, so it survives a URL reload and
 * cannot drift out of sync with the dates the ledger was actually fetched for.
 */
export const resolvePeriodKey = (
  startDate?: string | null,
  endDate?: string | null,
  today: Date = new Date(),
): PeriodKey => {
  const match = STATEMENT_PERIODS.find(period => {
    const range = period.range(today)
    return range.startDate === startDate && range.endDate === endDate
  })

  return match ? match.key : 'custom'
}
